// Intelligence.jsx — the new Intelligence Brief tab.
//
// Phases:
//   - list:       pick a bundle, generate today's brief, glance at the
//                 horizontal Recent Briefs rail.
//   - generating: cycling loading messages while /api/brief runs.
//   - detail:     render a specific brief (delegated to IntelligenceDetail).
//   - archive:    date-grouped full-width list of ALL briefs, with a
//                 bundle-filter chip row. Entered via the trailing "View all"
//                 tile at the end of the Recent Briefs rail. Transient: not
//                 persisted across tab switches (feels like a drill-in, not
//                 a separate destination).
//
// History is kept in briefStore (localStorage, MVP). If we later want
// cross-device sync we'll mirror to Supabase without touching this UI.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../components/Icon.jsx';
import { CheckIcon } from '../components/CheckIcon.jsx';
import { ProcessingOrb } from '../components/ProcessingOrb.jsx';
import { BUNDLES, bundleMeta } from '../lib/briefBundles.js';
import { briefStore, displayEditionFor } from '../lib/briefStore.js';
import * as briefJobStore from '../lib/briefJobStore.js';
import { generateBrief } from '../lib/briefApi.js';
import { AIConsentDeclinedError } from '../lib/aiConsent.js';
import { PaywallRequiredError } from '../lib/llm.js';
import { useEntitlements, useUsageCounts, presentPaywall } from '../lib/subscription.js';
import { IntelligenceDetail } from './IntelligenceDetail.jsx';
import { sfx } from '../lib/sfx.js';
import { todayIsoKey } from '../lib/dateKeys.js';
import * as audioStore from '../lib/audioPlayerStore.js';

// Where we remember the last page the Intelligence tab was showing.
// The tab currently unmounts when the user navigates away (App renders it
// inside a `{tab === 'intelligence' && ...}` gate), so without this hint
// the tab would always reset to the list on return. We persist just the
// phase and the id — the actual brief object is re-resolved from
// briefStore on mount so we never show a stale copy.
const INTEL_NAV_KEY = 'proxera.intel.nav.v1';
function readLastNav() {
  try {
    const raw = localStorage.getItem(INTEL_NAV_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch { return null; }
}
function writeLastNav(phase, briefId) {
  try {
    // Never persist transient phases — they'd strand the user on a
    // spinner or error screen forever on next visit. Only `list` and
    // `detail` are durable.
    const phaseToStore = (phase === 'detail') ? 'detail' : 'list';
    localStorage.setItem(
      INTEL_NAV_KEY,
      JSON.stringify({ phase: phaseToStore, briefId: phaseToStore === 'detail' ? briefId : null })
    );
  } catch {}
}

const LOADING_MESSAGES = [
  'Consulting primary sources...',
  "Synthesizing today's most important insights...",
  'Filtering signal from noise...',
  'Finalizing your briefing...',
];

function humanDate(iso) {
  if (!iso) return '';
  try {
    // Parse parts explicitly as local time. Passing an ISO string to `new Date`
    // is inconsistent across runtimes — iOS WebKit in particular has treated
    // "YYYY-MM-DDT00:00:00" as UTC in past versions, which pushed the label
    // one day ahead for users east of UTC. Constructing from parts avoids that.
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return iso;
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return iso; }
}

export function Intelligence({ isGuest, onRequestSignIn, openBriefId, onBriefOpened }) {
  const [prefs, setPrefs] = useState(() => briefStore.getPrefs());
  const [selectedBundle, setSelectedBundle] = useState(() => briefStore.getPrefs().defaultBundle || 'technology');
  const [customFocus, setCustomFocus] = useState('');
  const [bundlePickerOpen, setBundlePickerOpen] = useState(false);

  // Initial phase / detail resolution — runs ONCE synchronously on mount so
  // the first paint is already on the correct screen (no flash of the list
  // before an effect jumps into detail). Priority:
  //   1. If a brief generation is actively running in briefJobStore (because
  //      the user kicked one off, left the tab, and came back before it
  //      finished), snap straight back to the generating screen so the
  //      animation resumes instead of pretending nothing is happening.
  //   2. If a brief just finished while we were unmounted, open it. This
  //      covers the "generation completed while on another tab" case so the
  //      user lands on their fresh brief instead of the list.
  //   3. If audio is loaded (playing OR paused) and we have that brief
  //      locally, show its detail page. Honors the user rule: "if a brief
  //      is playing, clicking back to the tab shows its page".
  //   4. Otherwise restore the last page they had open in this tab.
  //   5. Otherwise start on the list.
  const initialPhaseAndBrief = useMemo(() => {
    try {
      if (briefJobStore.getActive()) {
        return { phase: 'generating', brief: null };
      }
      const result = briefJobStore.getLastResult();
      if (result && result.brief) {
        // Consume so a later remount does not re-open this same brief.
        briefJobStore.consumeLastResult();
        return { phase: 'detail', brief: result.brief };
      }
    } catch {}
    try {
      const audio = audioStore.getState?.();
      const playingId = audio?.brief?.id;
      if (playingId) {
        const b = briefStore.get(playingId);
        if (b) return { phase: 'detail', brief: b };
      }
    } catch {}
    const last = readLastNav();
    if (last?.phase === 'detail' && last.briefId) {
      const b = briefStore.get(last.briefId);
      if (b) return { phase: 'detail', brief: b };
    }
    return { phase: 'list', brief: null };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [phase, setPhase] = useState(initialPhaseAndBrief.phase); // list | generating | detail | error
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [detailBrief, setDetailBrief] = useState(initialPhaseAndBrief.brief);
  const [briefs, setBriefs] = useState(() => briefStore.listBriefs());

  // Persist nav state whenever the user lands on list or detail. Transient
  // phases (generating/error) are intentionally skipped inside writeLastNav.
  useEffect(() => {
    writeLastNav(phase, detailBrief?.id || null);
  }, [phase, detailBrief]);

  const today = todayIsoKey();
  // For the Custom bundle, we pass the user's current focus string so the
  // store can decide whether there's a same-concept brief from today. For
  // standard bundles customFocus is ignored in the lookup.
  const todaysBriefForBundle = useMemo(
    () => briefStore.todayForBundle(selectedBundle, customFocus),
    [selectedBundle, customFocus, briefs]
  );

  // Cycle the loading message every 6s so the user is never watching a
  // static spinner during the 30-90s research window.
  useEffect(() => {
    if (phase !== 'generating') return;
    const id = setInterval(() => {
      setLoadingMsgIndex(i => (i + 1) % LOADING_MESSAGES.length);
    }, 6000);
    return () => clearInterval(id);
  }, [phase]);

  // React to briefJobStore transitions. Covers the cross-mount case: we
  // initialized into 'generating' because a job was running, then the job
  // finishes (either success or failure). The job's own code updates the
  // store, and this subscription flips us into detail / error with the
  // freshly returned brief. Also handles the reverse: we were on list,
  // another surface dispatched a generation (not a path we take today,
  // but the store is global and this stays honest if we add one later).
  useEffect(() => {
    const unsub = briefJobStore.subscribe(() => {
      const active = briefJobStore.getActive();
      if (active) {
        // A job just started somewhere. If we are not already on the
        // generating screen, show it.
        setPhase(p => (p === 'generating' ? p : 'generating'));
        return;
      }
      // No active job. If we were showing the generating screen, the
      // job we were attached to just finished — resolve it.
      const result = briefJobStore.getLastResult();
      if (!result) return;
      if (result.brief) {
        briefJobStore.consumeLastResult();
        refreshBriefs();
        setDetailBrief(result.brief);
        setPhase('detail');
        sfx.briefingReady && sfx.briefingReady();
        // Fire the Brief Ready notification. iOS auto-suppresses the
        // banner when the app is in the foreground, so this is only
        // visible to users who navigated away while the job was still
        // running.
        import('../lib/notifications.js').then(m => m.fireBriefReady({
          bundleName: bundleMeta(result.brief.bundle)?.name,
          insightCount: Array.isArray(result.brief.insights) ? result.brief.insights.length : 8,
        })).catch(() => {});
      } else if (result.error) {
        briefJobStore.consumeLastResult();
        setErrorMsg(result.error?.message || 'Could not generate your brief. Try again.');
        setPhase('error');
      }
    });
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const refreshBriefs = () => setBriefs(briefStore.listBriefs());

  const setDefaultBundle = (id) => {
    const updated = briefStore.updatePrefs({ defaultBundle: id });
    setPrefs(updated);
  };

  const openBundlePicker = () => {
    sfx.sheetOpen && sfx.sheetOpen();
    setBundlePickerOpen(true);
  };
  const closeBundlePicker = () => {
    sfx.sheetClose && sfx.sheetClose();
    setBundlePickerOpen(false);
  };
  const pickBundle = (id) => {
    setSelectedBundle(id);
    setDefaultBundle(id);
    closeBundlePicker();
  };

  const openBrief = (brief) => {
    sfx.nav && sfx.nav();
    setDetailBrief(brief);
    setPhase('detail');
  };

  // When the caller (typically the now-playing mini bar) asks us to open a
  // specific brief by id, resolve it from store and navigate into detail.
  useEffect(() => {
    if (!openBriefId) return;
    const target = briefStore.get(openBriefId) || briefs.find(b => b.id === openBriefId);
    if (target) {
      setDetailBrief(target);
      setPhase('detail');
    }
    if (typeof onBriefOpened === 'function') onBriefOpened();
  }, [openBriefId]); // eslint-disable-line react-hooks/exhaustive-deps

  const backToList = () => {
    sfx.nav && sfx.nav();
    setDetailBrief(null);
    setPhase('list');
  };

  const openArchive = () => {
    sfx.nav && sfx.nav();
    setPhase('archive');
  };

  const generate = async () => {
    if (isGuest) {
      sfx.nav && sfx.nav();
      onRequestSignIn && onRequestSignIn();
      return;
    }
    // If already generated today for this bundle, open it directly.
    if (todaysBriefForBundle) {
      openBrief(todaysBriefForBundle);
      return;
    }
    // If a job is already running (tab was just remounted into generating),
    // do nothing — the subscription will route the completion.
    if (briefJobStore.getActive()) return;

    const edition = briefStore.nextEditionFor(selectedBundle);
    // Look up the most recent prior brief for THIS bundle so the API can
    // tighten the web_search window to [lastEdition, now] (capped at first of
    // month). Without this the model re-scans a rolling 7-day window and
    // yesterday's tech brief overlaps today's by six days of sources.
    const previousBrief = briefStore.latestBriefForBundle(selectedBundle, customFocus);
    setErrorMsg('');
    setPhase('generating');
    setLoadingMsgIndex(0);
    sfx.toast && sfx.toast();
    sfx.startThinking && sfx.startThinking();

    // Hand the actual work to briefJobStore so the generation survives
    // the Intelligence tab unmounting. The store's `finally` runs AFTER
    // we update `lastResult`, so the subscription in the effect above
    // will navigate us into detail/error whether we are mounted or not.
    try {
      await briefJobStore.startJob(
        {
          bundleId: selectedBundle,
          customFocus,
          edition,
          label: selectedBundle === 'custom' ? (customFocus || '').trim() : '',
        },
        async () => {
          const brief = await generateBrief({
            bundleId: selectedBundle,
            customFocus,
            edition,
            previousBrief,
          });
          if (!brief) throw new Error('No brief returned.');
          const saved = briefStore.save({
            ...brief,
            bundle: selectedBundle,
            // Persist the focus string used so the store can do same-concept
            // dedup if the user comes back with a related query today.
            customTopicName: selectedBundle === 'custom' ? customFocus.trim() : null,
            edition: brief.edition || edition,
            date: brief.date || today,
          });
          return saved;
        },
      );
      // Success path is handled by the briefJobStore subscription — it
      // flips phase → 'detail' and sets detailBrief. Nothing else to do.
    } catch (e) {
      // User declined the AI consent prompt — silently revert to the
      // bundle list rather than show an error screen.
      if (e instanceof AIConsentDeclinedError) {
        setPhase('list');
        return;
      }
      // Free-tier cap hit. The global PaywallSheet already opened from
      // the briefApi 402 handler — bouncing to the bundle list keeps the
      // UI calm.
      if (e instanceof PaywallRequiredError) {
        setPhase('list');
        return;
      }
      // Error path is also wired through the subscription, but in case we
      // ever unsubscribe before it fires, keep a local fallback.
      if (!briefJobStore.getActive()) {
        setErrorMsg(e?.message || 'Could not generate your brief. Try again.');
        setPhase('error');
      }
    } finally {
      sfx.stopThinking && sfx.stopThinking();
    }
  };

  // ── Detail view (delegates rendering) ────────────────────────────
  if (phase === 'detail' && detailBrief) {
    return <IntelligenceDetail brief={detailBrief} onBack={backToList}/>;
  }

  // ── Generating view ──────────────────────────────────────────────
  if (phase === 'generating') {
    const current = LOADING_MESSAGES[loadingMsgIndex];
    return (
      <div className="fade-in" style={{ padding: '40px 24px', textAlign: 'center' }}>
        <div className="eyebrow" style={{ color: 'var(--gold-2)' }}>Generating</div>
        <h1 className="brief-serif" style={{ fontSize: 28, color: 'var(--fg-1)', margin: '8px 0 6px', letterSpacing: '-0.01em' }}>
          The Briefing
        </h1>
        <div className="gold-hairline" style={{ margin: '14px auto', width: '40%' }}/>

        <div style={{
          marginTop: 30, padding: 28, borderRadius: 22,
          background: 'radial-gradient(circle at 100% 0%, rgba(45,156,255,0.15), transparent 55%), rgba(19,21,25,0.7)',
          backdropFilter: 'blur(30px) saturate(180%)',
          border: '1px solid var(--stroke-1)',
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
        }}>
          <ProcessingOrb size={64} />
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 13.5, color: 'var(--fg-2)', lineHeight: 1.6, minHeight: 42, marginTop: 18, textAlign: 'center' }}>
            {current}
          </div>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--fg-4)', marginTop: 14, lineHeight: 1.5 }}>
            Deep research takes 30 to 90 seconds.
          </div>
        </div>

        {/* Tip card — kept visually distinct from the status card above so it
            reads as a standing assurance, not part of the cycling copy. The
            user can leave the tab without losing progress; the job lives in
            briefJobStore and reattaches on return. No icon — the assurance
            is the copy itself, and the check was redundant with the
            generating spinner just above. */}
        <div style={{
          marginTop: 14, padding: '12px 16px', borderRadius: 14,
          background: 'rgba(var(--accent-rgb), 0.06)',
          border: '1px solid rgba(var(--accent-rgb), 0.18)',
          textAlign: 'center',
        }}>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, lineHeight: 1.5, color: 'var(--fg-2)' }}>
            Feel free to leave this tab. Your brief will keep generating in the background and open automatically when it is ready.
          </div>
        </div>
      </div>
    );
  }

  // ── Error view ───────────────────────────────────────────────────
  if (phase === 'error') {
    // Rate-limit errors show a premium-style upsell instead of the generic
    // "could not generate" header. The server returns a specific message so
    // we pattern-match on "faster than the free tier" or 429 wording.
    const isRateLimit = /free tier|too fast|Proxera Premium|rate limit/i.test(errorMsg || '');
    return (
      <div className="fade-in" style={{ padding: '40px 24px', textAlign: 'center' }}>
        {isRateLimit ? (
          <>
            <div className="eyebrow" style={{ color: 'var(--gold-2)' }}>Slow down. Almost there.</div>
            <h1 className="brief-serif" style={{ fontSize: 26, color: 'var(--fg-1)', margin: '8px 0 6px', letterSpacing: '-0.01em' }}>
              You're briefing fast
            </h1>
            <div className="gold-hairline" style={{ margin: '14px auto 8px', width: '40%' }}/>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13.5, color: 'var(--fg-2)', lineHeight: 1.6, marginTop: 12, maxWidth: 320, marginLeft: 'auto', marginRight: 'auto' }}>
              Free accounts are capped at a handful of deep briefs per minute. Give it about a minute and try again, or unlock unlimited on-demand briefs with Proxera Premium.
            </p>
          </>
        ) : (
          <>
            <div className="eyebrow" style={{ color: '#FF8A9E' }}>Could not generate</div>
            <h1 className="brief-serif" style={{ fontSize: 24, color: 'var(--fg-1)', margin: '8px 0 6px' }}>
              Let us try that again
            </h1>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--fg-3)', lineHeight: 1.55, marginTop: 12 }}>
              {errorMsg || 'The research service was unreachable. Check your connection and retry.'}
            </p>
          </>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 20 }}>
          <button className="btn btn-ghost" onClick={() => setPhase('list')} style={{ padding: '10px 16px' }}>
            Back
          </button>
          <button className="btn btn-primary" onClick={generate} style={{ padding: '10px 18px' }}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  // ── Archive view ─────────────────────────────────────────────────
  // Full-width, date-grouped list of all briefs plus a bundle-filter chip row.
  // Entered from the "View all" tile at the end of the Recent Briefs rail.
  // Kept transient on purpose: leaving the tab and returning drops back to
  // the list view, same as hitting Back. The Back button at the top is the
  // primary exit.
  if (phase === 'archive') {
    return (
      <IntelligenceArchive
        briefs={briefs}
        onBack={backToList}
        onOpenBrief={openBrief}
      />
    );
  }

  // ── List view (default) ──────────────────────────────────────────
  const currentMeta = bundleMeta(selectedBundle);
  const hasToday = !!todaysBriefForBundle;

  return (
    <div className="fade-in" style={{ paddingTop: 4 }}>
      {/* Header */}
      <div style={{ padding: '10px 20px 6px', textAlign: 'center' }}>
        <div className="eyebrow" style={{ fontSize: 9, color: 'var(--gold-2)' }}>Intelligence</div>
        <h1 className="brief-serif" style={{ marginTop: 6, fontSize: 28, color: 'var(--fg-1)', letterSpacing: '-0.01em', lineHeight: 1.1 }}>
          Intelligence Brief
        </h1>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 11.5, color: 'var(--fg-3)', marginTop: 4, fontStyle: 'italic' }}>
          {humanDate(today)}
        </div>
        <div className="gold-hairline" style={{ margin: '14px auto 0', width: '60%' }}/>
      </div>

      {/* Bundle picker */}
      <div style={{ padding: '18px 20px 4px' }}>
        <div className="eyebrow" style={{ marginBottom: 8, paddingLeft: 4 }}>Today's Lens</div>
        <button
          onClick={openBundlePicker}
          style={{
            width: '100%',
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '14px 16px', borderRadius: 18,
            background: 'rgba(19,21,25,0.65)',
            backdropFilter: 'blur(24px) saturate(160%)',
            border: '1px solid var(--stroke-2)',
            color: 'var(--fg-1)',
            cursor: 'pointer', textAlign: 'left',
          }}
        >
          <div style={{
            width: 42, height: 42, borderRadius: 13,
            background: `linear-gradient(135deg, ${currentMeta.accent}2E, ${currentMeta.accent}10)`,
            border: `1px solid ${currentMeta.accent}55`,
            display: 'grid', placeItems: 'center',
          }}>
            <Icon name="brain" size={18} color={currentMeta.accent}/>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--fg-1)' }}>
              {currentMeta.name}
            </div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 11.5, color: 'var(--fg-3)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {currentMeta.description}
            </div>
          </div>
          <Icon name="chevron-d" size={16} color="var(--fg-4)"/>
        </button>

        {selectedBundle === 'custom' && (
          <div style={{ marginTop: 10 }}>
            <textarea
              value={customFocus}
              onChange={e => setCustomFocus(e.target.value)}
              placeholder="Describe the focus for your custom briefing. Be specific about domains, source types, and the angle you care about."
              style={{
                width: '100%', minHeight: 80,
                padding: '12px 14px', borderRadius: 14,
                background: 'rgba(12,13,15,0.6)',
                border: '1px solid var(--stroke-2)',
                color: 'var(--fg-1)', resize: 'vertical',
                fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: 1.5,
                outline: 'none',
              }}
            />
          </div>
        )}
      </div>

      {/* Free-tier usage chip — only renders for free users so paid users
          aren't reminded of a cap they don't have. The key is bumped on
          successful generation so the count updates without a tab switch. */}
      <BriefUsageChip refreshKey={briefs.length}/>

      {/* Primary CTA */}
      <div style={{ padding: '16px 20px 20px' }}>
        <button
          onClick={generate}
          disabled={phase === 'generating' || (selectedBundle === 'custom' && !customFocus.trim() && !hasToday)}
          style={{
            width: '100%',
            padding: '16px 18px', borderRadius: 16,
            background: hasToday ? 'rgba(45,156,255,0.14)' : 'var(--grad-gold)',
            border: hasToday ? '1px solid var(--gold-ring)' : 'none',
            color: hasToday ? 'var(--gold-2)' : '#06152B',
            fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 14,
            letterSpacing: 0.04, cursor: 'pointer',
            boxShadow: hasToday ? 'none' : '0 0 22px rgba(45,156,255,0.4), inset 0 1px 0 rgba(255,255,255,0.35)',
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10,
          }}
        >
          <Icon name={hasToday ? 'book' : 'sparkles'} size={16} color={hasToday ? 'var(--gold-2)' : '#06152B'}/>
          {hasToday ? "View Today's Brief" : "Generate Today's Brief"}
        </button>

        {isGuest && (
          <div style={{
            marginTop: 10, padding: '10px 12px',
            borderRadius: 12,
            background: 'rgba(45,156,255,0.10)',
            border: '1px solid rgba(45,156,255,0.28)',
            display: 'flex', gap: 10, alignItems: 'flex-start',
          }}>
            <Icon name="lock" size={14} color="#2D9CFF"/>
            <div style={{ flex: 1, fontFamily: 'var(--font-body)', fontSize: 11.5, color: 'var(--fg-2)', lineHeight: 1.5 }}>
              Sign in to generate Intelligence Briefs. Your habits, briefings, and history will sync across devices.
            </div>
            <button
              onClick={() => { sfx.nav && sfx.nav(); onRequestSignIn && onRequestSignIn(); }}
              style={{
                padding: '6px 10px', borderRadius: 999,
                background: 'rgba(45,156,255,0.15)',
                border: '1px solid var(--gold-ring)',
                color: 'var(--gold-2)',
                fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 10,
                letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer',
              }}
            >
              Sign in
            </button>
          </div>
        )}
      </div>

      {/* Recent briefs
          --------------------------------------------------------------
          "View all" lives in the section header, right-aligned. Earlier
          it sat as a trailing tile at the end of the horizontal rail,
          but as more briefs accumulate it drifts further off-screen
          and forces the user to scroll past the whole history to reach
          the archive. Header-level placement keeps it at a fixed,
          always-visible anchor regardless of list length. */}
      {briefs.length > 0 && (
        <div style={{ padding: '6px 0 20px' }}>
          <div style={{
            padding: '0 24px', marginBottom: 10,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <div className="eyebrow">Recent Briefs</div>
            <button
              onClick={openArchive}
              type="button"
              style={{
                appearance: 'none', WebkitAppearance: 'none',
                background: 'transparent', border: 'none', padding: 0, margin: 0,
                display: 'inline-flex', alignItems: 'center', gap: 4,
                fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 10,
                letterSpacing: '0.14em', textTransform: 'uppercase',
                color: 'var(--gold-2)', cursor: 'pointer',
              }}
            >
              View all
              <span style={{ fontFamily: 'var(--font-body)', fontWeight: 500, fontSize: 10, color: 'var(--fg-4)', letterSpacing: 0, textTransform: 'none' }}>
                ({briefs.length})
              </span>
              <Icon name="chevron-r" size={12} color="var(--gold-2)"/>
            </button>
          </div>
          <div style={{
            display: 'flex', gap: 12,
            overflowX: 'auto', overflowY: 'hidden',
            padding: '2px 20px 10px',
            scrollbarWidth: 'none',
            WebkitOverflowScrolling: 'touch',
          }}>
            {briefs.slice(0, 12).map(b => (
              <BriefCard
                key={b.id}
                brief={b}
                onOpen={openBrief}
                displayEdition={displayEditionFor(b, briefs)}
              />
            ))}
          </div>
        </div>
      )}

      {/* First-time empty state */}
      {briefs.length === 0 && !isGuest && (
        <div style={{ padding: '18px 24px 24px' }}>
          <div style={{
            padding: 20, borderRadius: 18,
            background: 'rgba(19,21,25,0.55)', border: '1px solid var(--stroke-1)',
            display: 'flex', gap: 12, alignItems: 'flex-start',
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 12,
              background: 'var(--gold-soft)',
              border: '1px solid var(--gold-ring)',
              display: 'grid', placeItems: 'center',
              flexShrink: 0,
            }}>
              <Icon name="brain" size={16} color="var(--gold-2)"/>
            </div>
            <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--fg-3)', lineHeight: 1.55 }}>
              Your first brief is one tap away. Pick a lens above, then generate. Briefs take roughly a minute because we pull from primary sources live.
            </div>
          </div>
        </div>
      )}

      {/* Bundle picker sheet */}
      {bundlePickerOpen && (
        <BundlePickerSheet
          selectedId={selectedBundle}
          onPick={pickBundle}
          onClose={closeBundlePicker}
        />
      )}
    </div>
  );
}

// ── BundlePickerSheet ─────────────────────────────────────────────────
function BundlePickerSheet({ selectedId, onPick, onClose }) {
  return (
    <div
      style={{
        position: 'absolute', inset: 0, zIndex: 150,
        display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
      }}
    >
      <div
        onClick={onClose}
        style={{
          position: 'absolute', inset: 0,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(8px)',
          animation: 'fadeIn 180ms ease-out',
        }}
      />
      <div style={{
        position: 'relative',
        // Clear the tab bar (68px height + 18px offset = 86px from the
        // viewport bottom) plus a 16px breathing room. Without this the
        // bottom of the sheet sat behind the tab bar and the "Custom"
        // option got clipped.
        margin: '0 10px calc(102px + env(safe-area-inset-bottom, 0px))',
        padding: '16px 14px 18px',
        borderRadius: 22,
        background: 'radial-gradient(circle at 100% 0%, rgba(45,156,255,0.18), transparent 55%), rgba(19,21,25,0.95)',
        backdropFilter: 'blur(30px) saturate(180%)',
        border: '1px solid var(--stroke-1)',
        boxShadow: '0 24px 64px rgba(0,0,0,0.6), inset 0 1px 0 rgba(255,255,255,0.06)',
        maxHeight: '75vh', overflowY: 'auto',
      }}>
        <div style={{ width: 40, height: 4, borderRadius: 999, background: 'rgba(255,255,255,0.16)', margin: '0 auto 14px' }}/>
        <div className="eyebrow" style={{ color: 'var(--gold-2)', textAlign: 'center', marginBottom: 10 }}>
          Select a lens
        </div>

        {[...BUNDLES, { id: 'custom', name: 'Custom', description: 'Enter your own topic focus', accent: '#41B3FF' }].map(b => {
          const selected = b.id === selectedId;
          return (
            <button
              key={b.id}
              onClick={() => onPick(b.id)}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: 12,
                width: '100%', padding: '12px 14px', marginBottom: 8,
                borderRadius: 14,
                background: selected ? `linear-gradient(135deg, ${b.accent}2E, ${b.accent}10)` : 'rgba(255,255,255,0.03)',
                border: `1px solid ${selected ? b.accent + '88' : 'var(--stroke-2)'}`,
                color: 'var(--fg-1)', cursor: 'pointer', textAlign: 'left',
              }}
            >
              <div style={{
                width: 36, height: 36, borderRadius: 11,
                background: `linear-gradient(135deg, ${b.accent}33, ${b.accent}10)`,
                border: `1px solid ${b.accent}55`,
                display: 'grid', placeItems: 'center',
                flexShrink: 0,
              }}>
                <Icon name="brain" size={16} color={b.accent}/>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 14, color: 'var(--fg-1)' }}>
                  {b.name}
                </div>
                <div style={{ fontFamily: 'var(--font-body)', fontSize: 11.5, color: 'var(--fg-3)', marginTop: 3, lineHeight: 1.45 }}>
                  {b.description}
                </div>
              </div>
              {selected && (
                <div style={{
                  width: 22, height: 22, borderRadius: 999,
                  background: b.accent,
                  display: 'grid', placeItems: 'center',
                  boxShadow: `0 0 10px ${b.accent}88`,
                  flexShrink: 0,
                }}>
                  <CheckIcon size={12} color="#0C0D0F" stroke={3}/>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── BriefCard ─────────────────────────────────────────────────────────
// Single card used by both the horizontal Recent Briefs rail and the
// archive's date-grouped list. When `fullWidth` is true we override the
// fixed 230px width from the .brief-card CSS class so the card fills the
// archive's column and the 3-line title has room to breathe.
//
// `displayEdition` is the sequential rank of this brief among its bundle
// siblings (1, 2, 3, …) — callers compute it via displayEditionFor so the
// label never shows gaps after failed generations. If omitted we fall back
// to the raw stored field for safety.
function BriefCard({ brief, onOpen, fullWidth, displayEdition }) {
  const m = bundleMeta(brief.bundle);
  const first = Array.isArray(brief.insights) && brief.insights[0];
  const editionLabel = displayEdition || brief.edition || 1;
  return (
    <div
      className="brief-card"
      onClick={() => onOpen(brief)}
      style={fullWidth ? { width: '100%' } : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <div style={{ width: 7, height: 7, borderRadius: 999, background: m.accent, boxShadow: `0 0 6px ${m.accent}` }}/>
        <div style={{ fontFamily: 'var(--font-display)', fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', color: m.accent, textTransform: 'uppercase' }}>
          {m.name}
        </div>
        <div style={{ flex: 1 }}/>
        <div style={{ fontFamily: 'var(--font-body)', fontSize: 10, color: 'var(--fg-4)' }}>
          {humanDate((brief.generatedAt || '').slice(0, 10))}
        </div>
      </div>
      <div className="brief-serif" style={{
        fontSize: 15, color: 'var(--fg-1)', lineHeight: 1.25,
        display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {first?.title || brief.title || 'Untitled'}
      </div>
      <div style={{ fontFamily: 'var(--font-body)', fontSize: 10.5, color: 'var(--fg-4)' }}>
        Edition {editionLabel} · {Array.isArray(brief.insights) ? brief.insights.length : 0} insights
      </div>
    </div>
  );
}

// ── IntelligenceArchive ───────────────────────────────────────────────
// Full-width, vertically-scrolling, date-grouped list of every brief the
// user has. A horizontal chip row at the top filters by bundle without
// leaving the view (no second page load, no lost scroll position).
//
// Grouping buckets (newest first):
//   - Today
//   - Yesterday
//   - Earlier This Week    (2 to 6 days ago)
//   - "Month YYYY"         (older; one bucket per calendar month)
//
// Buckets with zero matches after filtering collapse entirely so the list
// never shows an empty section header.
function IntelligenceArchive({ briefs, onBack, onOpenBrief }) {
  // 'all' plus every bundle id. Order matches the standard bundle picker,
  // with Custom tacked on at the end.
  const [filter, setFilter] = useState('all');

  const filtered = useMemo(() => {
    if (filter === 'all') return briefs;
    return briefs.filter(b => b.bundle === filter);
  }, [briefs, filter]);

  const groups = useMemo(() => groupBriefsByDate(filtered), [filtered]);

  // "All + the 5 standard bundles + Custom" for the chip row. We always show
  // the full set even when some chips would currently match zero briefs, so
  // the UI doesn't flicker as the user generates new briefs.
  const chips = [
    { id: 'all', name: 'All', accent: 'var(--gold-2)' },
    ...BUNDLES,
    { id: 'custom', name: 'Custom', accent: '#41B3FF' },
  ];

  return (
    <div className="fade-in" style={{ paddingTop: 4 }}>
      {/* Header with back button */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '10px 16px 4px',
      }}>
        <button
          onClick={onBack}
          style={{
            width: 36, height: 36, borderRadius: 12,
            background: 'rgba(19,21,25,0.6)',
            border: '1px solid var(--stroke-2)',
            color: 'var(--fg-1)',
            display: 'grid', placeItems: 'center',
            cursor: 'pointer',
          }}
          aria-label="Back"
        >
          <Icon name="chevron-l" size={18} color="var(--fg-1)"/>
        </button>
        <div style={{ flex: 1 }}>
          <div className="eyebrow" style={{ fontSize: 9, color: 'var(--gold-2)' }}>Intelligence</div>
          <div className="brief-serif" style={{
            fontSize: 22, color: 'var(--fg-1)', letterSpacing: '-0.01em', lineHeight: 1.1,
          }}>
            Recent Briefs
          </div>
        </div>
        <div style={{
          fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--fg-4)',
          paddingRight: 6,
        }}>
          {filtered.length} of {briefs.length}
        </div>
      </div>

      <div className="gold-hairline" style={{ margin: '10px 20px 12px' }}/>

      {/* Bundle filter chips, horizontally scrollable. Keeps the same
          left-to-right affordance the rail uses — feels like the same kind
          of control, just in a filter role. */}
      <div style={{
        display: 'flex', gap: 8,
        overflowX: 'auto', overflowY: 'hidden',
        padding: '2px 20px 14px',
        scrollbarWidth: 'none',
        WebkitOverflowScrolling: 'touch',
      }}>
        {chips.map(c => {
          const selected = filter === c.id;
          return (
            <button
              key={c.id}
              onClick={() => { sfx.nav && sfx.nav(); setFilter(c.id); }}
              style={{
                flex: '0 0 auto',
                padding: '7px 14px', borderRadius: 999,
                background: selected
                  ? `linear-gradient(135deg, ${c.accent}33, ${c.accent}12)`
                  : 'rgba(19,21,25,0.55)',
                border: `1px solid ${selected ? c.accent + '99' : 'var(--stroke-2)'}`,
                color: selected ? c.accent : 'var(--fg-2)',
                fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 11,
                letterSpacing: '0.1em', textTransform: 'uppercase',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {c.name}
            </button>
          );
        })}
      </div>

      {/* Empty state when the filter matches zero briefs. The bundle name
          tells the user exactly what they filtered to, and the button lets
          them reset without thumbing back through the chip row. */}
      {filtered.length === 0 && (
        <div style={{ padding: '24px 24px 40px', textAlign: 'center' }}>
          <div style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--fg-3)', lineHeight: 1.55 }}>
            No briefs in{' '}
            <span style={{ color: 'var(--fg-1)' }}>
              {chips.find(c => c.id === filter)?.name || filter}
            </span>
            {' '}yet.
          </div>
          {filter !== 'all' && (
            <button
              onClick={() => setFilter('all')}
              className="btn btn-ghost"
              style={{ marginTop: 14, padding: '8px 14px' }}
            >
              Show all
            </button>
          )}
        </div>
      )}

      {/* Grouped list. Each group header sticks around only when non-empty. */}
      <div style={{ padding: '0 20px 32px' }}>
        {groups.map(g => (
          <div key={g.label} style={{ marginBottom: 18 }}>
            <div className="eyebrow" style={{
              marginBottom: 8, paddingLeft: 4,
              color: 'var(--fg-3)',
            }}>
              {g.label}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {g.items.map(b => (
                <BriefCard
                  key={b.id}
                  brief={b}
                  onOpen={onOpenBrief}
                  fullWidth
                  // Rank against ALL briefs (not the archive's filtered set)
                  // so "Edition 3" means the third brief in that bundle
                  // overall, not the third one inside the current filter.
                  displayEdition={displayEditionFor(b, briefs)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── groupBriefsByDate ─────────────────────────────────────────────────
// Bucket an already-newest-first array of briefs into labeled date groups.
// Returns only non-empty groups, in display order, so the archive view can
// map straight over the result.
//
// The week bucket covers days 2-6 ago in local time. Anything older drops
// into a "Month YYYY" bucket. We explicitly use local time (not ISO/UTC) so
// the labels match what the user sees on their phone at 11pm in PT.
function groupBriefsByDate(briefs) {
  const now = new Date();
  const startOfLocalDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const todayStart = startOfLocalDay(now);
  const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
  const weekStart = new Date(todayStart.getTime() - 6 * 24 * 60 * 60 * 1000);

  const today = [];
  const yesterday = [];
  const thisWeek = [];
  const byMonth = new Map(); // "April 2026" → [...]

  for (const b of briefs) {
    const t = Date.parse(b.generatedAt || '');
    if (!isFinite(t)) {
      // Unknown date: drop into "Earlier" so nothing disappears.
      const key = 'Earlier';
      if (!byMonth.has(key)) byMonth.set(key, []);
      byMonth.get(key).push(b);
      continue;
    }
    const d = new Date(t);
    if (d >= todayStart) {
      today.push(b);
    } else if (d >= yesterdayStart) {
      yesterday.push(b);
    } else if (d >= weekStart) {
      thisWeek.push(b);
    } else {
      const key = d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      if (!byMonth.has(key)) byMonth.set(key, []);
      byMonth.get(key).push(b);
    }
  }

  const groups = [];
  if (today.length) groups.push({ label: 'Today', items: today });
  if (yesterday.length) groups.push({ label: 'Yesterday', items: yesterday });
  if (thisWeek.length) groups.push({ label: 'Earlier This Week', items: thisWeek });
  // Month buckets are inserted in the order we first saw them. Because the
  // input is newest-first, that order is already newest-month-first.
  for (const [label, items] of byMonth) {
    groups.push({ label, items });
  }
  return groups;
}

// ── BriefUsageChip ──────────────────────────────────────────────────
// Shows "X of Y briefs this week" for free users above the generate CTA.
// Tappable — opens the paywall directly when the user is curious about
// their cap (or has hit it).
function BriefUsageChip({ refreshKey = 0 }) {
  const ent = useEntitlements();
  const usage = useUsageCounts(refreshKey);
  if (ent.tier !== 'free') return null;
  if (usage.loading) return null;

  // Match the server default — overridable via env var, but the client
  // doesn't have access to those, so we use the launch number.
  const limit = 3;
  const used = Math.min(limit, usage.brief7d);
  const atCap = used >= limit;

  return (
    <div style={{ padding: '4px 20px 0' }}>
      <button
        type="button"
        onClick={() => presentPaywall({ reason: atCap ? 'brief_limit' : 'brief_chip' })}
        style={{
          width: '100%',
          padding: '10px 14px',
          borderRadius: 12,
          background: atCap ? 'rgba(255,140,140,0.08)' : 'rgba(255,255,255,0.04)',
          border: atCap ? '1px solid rgba(255,140,140,0.35)' : '1px solid var(--stroke-1)',
          color: 'var(--fg-2)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          fontFamily: 'var(--font-body)', fontSize: 12.5,
          cursor: 'pointer',
        }}
      >
        <span>
          <strong style={{ color: 'var(--fg-1)' }}>{used}</strong>
          {' '}of{' '}
          <strong style={{ color: 'var(--fg-1)' }}>{limit}</strong>
          {' '}free briefs this week
        </span>
        <span style={{
          fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 10,
          letterSpacing: '0.12em', textTransform: 'uppercase',
          color: atCap ? '#ff8c8c' : 'var(--gold-2)',
        }}>
          {atCap ? 'Upgrade' : 'See Pro'}
        </span>
      </button>
    </div>
  );
}
